import { MESSAGE } from '@vindr/services/constants/message.constant';
import { AxiosError } from 'axios';
import { mergeRegex, stringToRegExp } from '../converter';
import { DefaultErrorResponse } from '@vindr/services/interfaces';

/**
 * @example titleCase('hello world') // Hello World
 */
export function titleCase(str: string) {
  if (!str) return '';

  return str
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * @example sentenceCase('hello world') // Hello world
 */
export function sentenceCase(str: string) {
  if (!str) return '';
  str = str.toLowerCase().trim();

  return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * @example: hello_world => Hello World
 */
export function snakeToTitleCase(snakeStr: string): string {
  if (typeof snakeStr !== 'string') {
    console.error('typeof snakeStr is not a string', snakeStr);
    return snakeStr;
  }
  const words = snakeStr.split('_');

  return words
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

export function convertPhoneNumber(phone: string) {
  if (!phone) return '';

  const onlyAcceptNumber = phone.replace(/\D/g, '');

  const mapping = [
    {
      search: ['(84)', '(+84)', '+84', '84'],
      replace: '0',
    },
  ];

  let result = onlyAcceptNumber;

  mapping.forEach((item) => {
    const regArr = [];

    item.search.forEach((keySearch) => {
      regArr.push(stringToRegExp(keySearch));
    });

    const regex = new RegExp(`^${mergeRegex(regArr)}`);

    result = result.replace(regex, item.replace);
  });

  // Check duplicate replace text
  mapping.forEach((item) => {
    const isDuplicateReplace = new RegExp(
      `^${stringToRegExp(item.replace)}${stringToRegExp(item.replace)}`
    ).test(result);

    if (isDuplicateReplace) {
      result = result.replace(
        new RegExp(`^${stringToRegExp(item.replace)}`),
        ''
      );
    }
  });

  return result.replace(/\s/g, '');
}

function splitUnit(str: string) {
  const regex = /(\d+)(\D+)/g;
  const result = regex.exec(str);

  if (!result) return null;

  const [, value, unit] = result;

  return {
    value: Number(value),
    unit,
  };
}

export function viewableFileName(text: string, selector: HTMLElement) {
  if (!selector) return text || '';

  const rect = selector.getBoundingClientRect();
  const styles = getComputedStyle(selector);
  const paddingSelector = Number(
    splitUnit(styles.paddingLeft)?.value + splitUnit(styles.paddingRight)?.value
  );
  const extensionRegex = /(.+)(\.[^.]+$)/g;
  let [, fileName, extension] = extensionRegex.exec(text) || [];

  if (!fileName) fileName = text; //? If file name doesn't have extension

  const lengthTextShowAtEnd = 3;
  const endFileName = `...${fileName.slice(-lengthTextShowAtEnd)}${
    extension || ''
  }`;

  fileName = fileName.slice(0, -lengthTextShowAtEnd);

  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');

  context.font = styles.font;

  let fullName = fileName + endFileName;

  const elementWidth = rect.width - paddingSelector;
  let fullNameWidth = context.measureText(fullName).width;

  if (fullNameWidth <= elementWidth) return text;

  //? fileName.length > 1 to prevent infinite loop
  while (fullNameWidth > elementWidth && fileName.length > 1) {
    fileName = fileName.slice(0, -1);
    fullName = fileName + endFileName;
    fullNameWidth = context.measureText(fullName).width;
  }

  return `${fileName}${endFileName}`;
}

export function messageError<T extends DefaultErrorResponse>(
  error: AxiosError<T>
) {
  const msg = error?.response?.data;
  const localMessage = MESSAGE.ERROR[msg.error_code];

  return localMessage || sentenceCase(msg.error_message);
}

export function isVietnamese(searchText = '') {
  const vietnameseRegex =
    /[àáạảãâầấậẩẫăằắặẳẵèéẹẻẽêềếệểễìíịỉĩòóọỏõôồốộổỗơờớợởỡùúụủũưừứựửữỳýỵỷỹđ]/giu;
  return vietnameseRegex.test(searchText);
}

export function isAlphabet(str = '') {
  // Regular expression to match only alphabetic characters (a-z, A-Z)
  const regex = /^[a-zA-Z]+$/;
  return regex.test(str);
}

export function isNumber(str = '') {
  const regex = /^\d+$/;
  return regex.test(str);
}

/**
 * @example: insertTextAfterWords("This is a long title", 2, '<br>') => "This is <br> a long <br> title"
 */
export function insertTextAfterWords(
  string = '',
  size = 2,
  additionText = '<br>'
) {
  const words = string.split(' ');

  // Check if the size is valid
  if (size <= 0 || words.length <= size) return string;

  // Iterate over the words and add the additionText
  for (let i = size; i < words.length; i += size + 1) {
    words.splice(i, 0, additionText);
  }

  return words.join(' ');
}

export function removeEmptySpace(str = '') {
  return str.replace(/\s+/g, ' ');
}

/**
 * Auto add space at the last word of each string in the input.
 * Adds a space only when the last character of the current string is an alphabet or Vietnamese character,
 * and it ensures proper spacing between rest parameters.
 *
 * @example
 * autoSpace("Tiến sĩ", "A") => "Tiến sĩ A"
 * autoSpace("TS.", "A") => "TS.A"
 * autoSpace("Hello", "world") => "Hello world"
 * autoSpace("Hi", " ") => "Hi"
 */
export function autoSpace(...str: string[]) {
  const isValidChar = (char: string | undefined) =>
    char && (isAlphabet(char) || isVietnamese(char) || isNumber(char));

  return str.reduce((result, current, index) => {
    if (index === 0) return current; // First string, no need for concatenation logic

    const lastChar = result.at(-1);
    const firstChar = current[0];

    const validLastChar = isValidChar(lastChar);
    const validFirstChar = isValidChar(firstChar);

    if (validLastChar && validFirstChar) return `${result} ${current}`;

    // Otherwise, concatenate without space
    return result + current;
  }, '');
}
